// 메트로놈 전역 상태 — 화면을 옮겨도 소리가 끊기지 않도록 엔진을 앱 셸에 하나만 둔다.
// 설정(마지막 메트로놈 상태·연동 방식·일지 테마)과 내 곡(프리셋)은 계정에 저장한다.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import api from '../api/axios';
import { useDialog } from '../contexts/DialogProvider';
import MetronomeEngine from './MetronomeEngine';
import { defaultAccents, groupingOptions, parseGrouping } from './timeSignatures';
import SessionSaveDialog from './SessionSaveDialog';

export const DEFAULT_CONFIG = {
  bpm: 96,
  beats: 4,
  unit: 4,
  grouping: null,
  accents: [3, 1, 1, 1],
  subdivision: '1',
  sound: 'click',
  voice: 'off',
  countIn: false,
  trainer: { on: false, every: 4, step: 4, target: 120 },
  gap: { on: false, play: 2, mute: 1 },
  flash: true,
  timer: { on: false, minutes: 20 },
  pieceId: null,
};

const MIN_SESSION_SECONDS = 30; // 이보다 짧은 재생은 일지 연동 대상에서 뺀다

const MetronomeContext = createContext(null);
const ClockContext = createContext(0);

export const useMetronome = () => useContext(MetronomeContext);
// 재생 경과(초) — 매초 바뀌므로 필요한 컴포넌트만 구독하게 별도 컨텍스트로 둔다
export const useMetronomeClock = () => useContext(ClockContext);

// 박 이벤트 구독 (현재 박 표시·플래시용)
export function useBeat(handler) {
  const ctx = useMetronome();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => ctx?.engine.subscribe((ev) => ref.current(ev)), [ctx?.engine]);
}

// 저장된 설정을 현재 형식으로 보정 (필드 누락·박 수와 강세 길이 불일치 대비)
function normalizeConfig(raw) {
  const cfg = { ...DEFAULT_CONFIG, ...(raw || {}) };
  cfg.trainer = { ...DEFAULT_CONFIG.trainer, ...(raw?.trainer || {}) };
  cfg.gap = { ...DEFAULT_CONFIG.gap, ...(raw?.gap || {}) };
  cfg.timer = { ...DEFAULT_CONFIG.timer, ...(raw?.timer || {}) };
  // 음성 카운트는 '하나 둘 셋'(native)만 남았다. 예전에 저장한 '일 이 삼'(sino)은 켜진 상태로 옮긴다.
  if (cfg.voice !== 'off') cfg.voice = 'native';
  const groups = parseGrouping(cfg.grouping);
  if (!groups || groups.reduce((a, b) => a + b, 0) !== cfg.beats) cfg.grouping = null;
  if (!Array.isArray(cfg.accents) || cfg.accents.length !== cfg.beats) {
    cfg.accents = defaultAccents(parseGrouping(cfg.grouping) || [cfg.beats]);
  }
  return cfg;
}

// 박자표를 바꿀 때: 기본 묶음과 그 강세로 초기화
export function configForSignature(cfg, beats, unit) {
  const groups = groupingOptions(beats, unit)[0];
  return {
    ...cfg,
    beats,
    unit,
    grouping: groups.length > 1 ? groups.join('+') : null,
    accents: defaultAccents(groups),
  };
}

function pieceToConfig(cfg, piece) {
  const groups = parseGrouping(piece.grouping) || [piece.beats];
  const accents = piece.accents ? piece.accents.split(',').map(Number) : null;
  return normalizeConfig({
    ...cfg,
    bpm: piece.bpm,
    beats: piece.beats,
    unit: piece.beat_unit,
    grouping: groups.length > 1 ? groups.join('+') : null,
    accents: accents && accents.length === piece.beats ? accents : defaultAccents(groups),
    subdivision: piece.subdivision,
    pieceId: piece.id,
  });
}

export function configToPiece(cfg) {
  return {
    bpm: cfg.bpm,
    beats: cfg.beats,
    beat_unit: cfg.unit,
    grouping: cfg.grouping,
    accents: cfg.accents.join(','),
    subdivision: cfg.subdivision,
  };
}

export function MetronomeProvider({ user, children }) {
  const enabled = user?.role === 'MEMBER';
  const { toast } = useDialog();
  const engine = useMemo(() => new MetronomeEngine(), []);
  const [config, setConfigState] = useState(DEFAULT_CONFIG);
  const [linkMode, setLinkModeState] = useState('ask');
  const [pieces, setPieces] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sessionPrompt, setSessionPrompt] = useState(null);
  const [journalRevision, setJournalRevision] = useState(0);
  const startedAtRef = useRef(0);
  const configRef = useRef(config);
  const saveTimerRef = useRef(null);
  const wakeLockRef = useRef(null);

  configRef.current = config;

  // ----- 설정 로드/저장 -----
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    Promise.all([api.get('/practice-journal/settings'), api.get('/practice-journal/pieces')])
      .then(([s, p]) => {
        if (!alive) return;
        setConfigState(normalizeConfig(s.data.metronome));
        setLinkModeState(s.data.metronome_link || 'ask');
        setPieces(p.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { alive = false; };
  }, [enabled]);

  const saveSettings = useCallback((patch) => {
    api.put('/practice-journal/settings', patch).catch(() => {});
  }, []);

  // 메트로놈 상태는 조작이 잦으므로 잠시 멈춘 뒤 한 번에 저장한다
  const setConfig = useCallback((updater) => {
    setConfigState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveSettings({ metronome: next }), 1000);
      return next;
    });
  }, [saveSettings]);

  const setLinkMode = useCallback((mode) => {
    setLinkModeState(mode);
    saveSettings({ metronome_link: mode });
  }, [saveSettings]);

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  // ----- 엔진 연결 -----
  const groups = useMemo(() => parseGrouping(config.grouping) || [config.beats], [config.grouping, config.beats]);
  useEffect(() => {
    engine.setConfig({ ...config, groups });
  }, [engine, config, groups]);

  useEffect(() => engine.subscribe((ev) => {
    // 속도 트레이너가 올린 템포를 화면 상태에도 반영
    if (ev.type === 'bpm') setConfig((c) => ({ ...c, bpm: ev.bpm }));
    // 첫 박 진동 (안드로이드 등 지원 기기만 — iOS 웹은 진동 API가 없다)
    if (ev.beat === 0 && !ev.muted && configRef.current.flash && navigator.vibrate) navigator.vibrate(30);
  }), [engine, setConfig]);

  // 화면 꺼짐 방지 (지원 브라우저만, 앱이 다시 보이면 재요청)
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && document.visibilityState === 'visible') {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (e) { /* 절전 모드 등으로 거부되면 무시 */ }
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    requestWakeLock();
    const onVisible = () => { if (document.visibilityState === 'visible') requestWakeLock(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [playing, requestWakeLock]);

  // ----- 일지 연동 -----
  const recordSession = useCallback(async ({ seconds, bpm, pieceId }) => {
    try {
      await api.post('/practice-journal/entries/time', {
        date: dayjs().format('YYYY-MM-DD'),
        piece_id: pieceId || 0,
        seconds,
        bpm,
      });
      setJournalRevision((r) => r + 1);
      toast(`일지에 ${formatDuration(seconds)} 연습을 기록했어요.`);
    } catch (e) {
      toast('연습 기록을 저장하지 못했어요.', 'error');
    }
  }, [toast]);

  const stop = useCallback((reason) => {
    if (!engine.playing) return;
    engine.stop();
    setPlaying(false);
    const seconds = Math.round((performance.now() - startedAtRef.current) / 1000);
    setElapsed(0);
    const session = { seconds, bpm: configRef.current.bpm, pieceId: configRef.current.pieceId };
    if (reason === 'timer') toast(`연습 타이머 ${configRef.current.timer.minutes}분이 끝났어요.`, 'info');
    if (seconds < MIN_SESSION_SECONDS || linkMode === 'off') return;
    if (linkMode === 'auto') recordSession(session);
    else setSessionPrompt(session);
  }, [engine, linkMode, recordSession, toast]);

  const start = useCallback(async () => {
    if (engine.playing) return;
    try {
      await engine.start();
      startedAtRef.current = performance.now();
      setElapsed(0);
      setPlaying(true);
    } catch (e) {
      toast('소리를 재생할 수 없어요. 기기 음량과 무음 모드를 확인해 주세요.', 'error');
    }
  }, [engine, toast]);

  const toggle = useCallback(() => (engine.playing ? stop('user') : start()), [engine, start, stop]);

  // 경과 시간 + 연습 타이머 자동 정지
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      const sec = Math.floor((performance.now() - startedAtRef.current) / 1000);
      setElapsed(sec);
      const t = configRef.current.timer;
      if (t.on && sec >= t.minutes * 60) stop('timer');
    }, 1000);
    return () => clearInterval(id);
  }, [playing, stop]);

  // 로그아웃 등으로 셸이 내려가면 소리도 멈춘다
  useEffect(() => () => engine.stop(), [engine]);

  // ----- 내 곡(프리셋) -----
  const refreshPieces = useCallback(async () => {
    const res = await api.get('/practice-journal/pieces');
    setPieces(res.data);
    return res.data;
  }, []);

  const createPiece = useCallback(async (data) => {
    const res = await api.post('/practice-journal/pieces', data);
    setPieces((list) => [...list, res.data]);
    setJournalRevision((r) => r + 1);
    return res.data;
  }, []);

  const updatePiece = useCallback(async (id, data) => {
    const res = await api.put(`/practice-journal/pieces/${id}`, data);
    setPieces((list) => list.map((p) => (p.id === id ? res.data : p)));
    setJournalRevision((r) => r + 1);
    return res.data;
  }, []);

  const removePiece = useCallback(async (id) => {
    await api.delete(`/practice-journal/pieces/${id}`);
    setPieces((list) => list.filter((p) => p.id !== id));
    setConfig((c) => (c.pieceId === id ? { ...c, pieceId: null } : c));
    setJournalRevision((r) => r + 1);
  }, [setConfig]);

  const applyPiece = useCallback((piece) => {
    setConfig((c) => (piece ? pieceToConfig(c, piece) : { ...c, pieceId: null }));
  }, [setConfig]);

  // 시트의 뒤로가기 닫기는 AppDialog(공통)가 처리한다
  const openSheet = useCallback(() => setSheetOpen(true), []);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const value = useMemo(() => ({
    enabled, loaded, engine, config, setConfig, groups,
    linkMode, setLinkMode,
    pieces, refreshPieces, createPiece, updatePiece, removePiece, applyPiece,
    playing, start, stop, toggle,
    sheetOpen, openSheet, closeSheet,
    journalRevision, bumpJournal: () => setJournalRevision((r) => r + 1),
  }), [enabled, loaded, engine, config, setConfig, groups, linkMode, setLinkMode,
    pieces, refreshPieces, createPiece, updatePiece, removePiece, applyPiece,
    playing, start, stop, toggle, sheetOpen, openSheet, closeSheet, journalRevision]);

  return (
    <MetronomeContext.Provider value={value}>
      <ClockContext.Provider value={elapsed}>
        {children}
        <SessionSaveDialog
          session={sessionPrompt}
          pieces={pieces}
          onClose={() => setSessionPrompt(null)}
          onSave={(s) => { setSessionPrompt(null); recordSession(s); }}
        />
      </ClockContext.Provider>
    </MetronomeContext.Provider>
  );
}

export function formatClock(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatDuration(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
  if (m > 0) return `${m}분`;
  return `${sec}초`;
}
